import React, { useState, useEffect, useMemo } from 'react';
import { automationAPI } from '../../services/api';
import AutomationBuilder from '../AutomationBuilder/AutomationBuilder';
import { PROJECT_TYPES, PROJECT_TYPE_KEYS } from '../../constants/projectTypes';
import './automationManager.css';

// Each automation belongs to one category derived from its action types.
// Order here is the order categories show in the page.
const CATEGORIES = [
    { key: 'email',    label: 'Email Automations',     icon: '✉️',  match: ['SendEmail'] },
    { key: 'text',     label: 'Text / SMS Automations', icon: '💬',  match: ['SendText'] },
    { key: 'task',     label: 'Task Automations',       icon: '📋',  match: ['CreateTask'] },
    { key: 'status',   label: 'Status Change Automations', icon: '🔄', match: ['UpdateProjectStatus'] },
    { key: 'salesrep', label: 'Sales Rep Automations',  icon: '👤',  match: ['AssignSalesRep'] },
    { key: 'mixed',    label: 'Mixed Action Automations', icon: '🧩', match: [] },
    { key: 'other',    label: 'Other',                   icon: '⚙️',  match: [] },
];

function categorize(actionsJson) {
    try {
        const actions = JSON.parse(actionsJson || '[]');
        if (!actions.length) return 'other';
        const types = [...new Set(actions.map(a => a.type))];
        if (types.length > 1) return 'mixed';
        const t = types[0];
        const found = CATEGORIES.find(c => c.match.includes(t));
        return found ? found.key : 'other';
    } catch {
        return 'other';
    }
}

const FRIENDLY_TRIGGER = {
    ProjectCreation: 'Project Created',
    ProjectStatusChange: 'Status Change',
    TaskCompletion: 'Task Completed',
};

// Walk a condition tree (nested AND/OR groups) and collect every "type" condition.
// Shape: { logic, conditions: [{field, operator, value}], groups: [ ...same... ] }
function collectTypeConditions(node, out = []) {
    if (!node || typeof node !== 'object') return out;
    for (const c of node.conditions || []) {
        if (c?.field === 'type' && c.value) out.push(c);
    }
    for (const g of node.groups || []) collectTypeConditions(g, out);
    return out;
}

// Which job types an automation actually applies to.
//
// An automation with no type condition is universal — it fires for every job type,
// so it has to stay visible under any type filter rather than disappear. A
// not_equals condition is the inverse: everything except what it excludes. This is
// what makes the filter answer "what runs on a Commercial job?" instead of merely
// "what mentions Commercial?".
function jobTypesForAutomation(triggerConditionsJson) {
    let parsed;
    try {
        parsed = JSON.parse(triggerConditionsJson || '{}');
    } catch {
        return { types: new Set(PROJECT_TYPE_KEYS), universal: true };
    }

    const typeConditions = collectTypeConditions(parsed);
    if (typeConditions.length === 0) return { types: new Set(PROJECT_TYPE_KEYS), universal: true };

    const included = typeConditions.filter(c => c.operator !== 'not_equals').map(c => c.value);
    const excluded = typeConditions.filter(c => c.operator === 'not_equals').map(c => c.value);

    // An explicit equals list wins; otherwise start from everything and subtract.
    const base = included.length > 0 ? included : PROJECT_TYPE_KEYS;
    return {
        types: new Set(base.filter(t => !excluded.includes(t))),
        universal: false,
    };
}

// Text an automation can be found by. Covers the parts a person would actually
// remember — its name, what it does, and the message copy inside its actions —
// so searching "supplement" finds the rule whose email says it.
function searchableText(automation) {
    const parts = [automation.name, automation.description, automation.triggerType];
    try {
        for (const action of JSON.parse(automation.actions || '[]')) {
            parts.push(action.type, action.subject, action.message, action.title, action.newStatus, action.recipient);
        }
    } catch { /* malformed actions still match on name/description */ }
    return parts.filter(Boolean).join(' ').toLowerCase();
}

const AutomationManager = () => {
    const [automations, setAutomations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showBuilder, setShowBuilder] = useState(false);
    const [editingAutomation, setEditingAutomation] = useState(null);
    const [statusFilter, setStatusFilter] = useState('all'); // all | active | inactive
    const [categoryFilter, setCategoryFilter] = useState('all'); // all | email | text | task | status | salesrep | mixed | other
    const [jobTypeFilter, setJobTypeFilter] = useState('all'); // all | one of PROJECT_TYPE_KEYS
    const [search, setSearch] = useState('');
    const [openMenuId, setOpenMenuId] = useState(null);

    useEffect(() => {
        fetchAutomations();
    }, []);

    const fetchAutomations = async () => {
        try {
            setLoading(true);
            const response = await automationAPI.getAll();
            setAutomations(response.data);
        } catch (error) {
            console.error('Error fetching automations:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (automation) => {
        setEditingAutomation(automation);
        setShowBuilder(true);
    };

    const handleToggleActive = async (automation) => {
        try {
            const updated = { ...automation, isActive: !automation.isActive };
            await automationAPI.update(automation.id, updated);
            fetchAutomations();
        } catch (error) {
            console.error('Error toggling automation:', error);
        }
    };

    const handleDelete = async (automation) => {
        if (window.confirm(`Are you sure you want to delete "${automation.name}"?`)) {
            try {
                await automationAPI.delete(automation.id);
                fetchAutomations();
            } catch (error) {
                console.error('Error deleting automation:', error);
            }
        }
    };

    const handleCloseBuilder = () => {
        setShowBuilder(false);
        setEditingAutomation(null);
        fetchAutomations();
    };

    // Decorate each automation once with everything the filters need, so parsing
    // its JSON doesn't repeat on every keystroke.
    const decorated = useMemo(
        () => automations.map(a => {
            const { types, universal } = jobTypesForAutomation(a.triggerConditions);
            return {
                ...a,
                _category: categorize(a.actions),
                _jobTypes: types,
                _allJobTypes: universal,
                _search: searchableText(a),
            };
        }),
        [automations]
    );

    // Everything except the category filter. Kept separate so the category chips can
    // show counts for what the other filters have already narrowed to — a chip
    // reading "Email (12)" when a search only matches 2 of them is a lie.
    const preCategory = useMemo(() => {
        const terms = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
        return decorated.filter(a => {
            if (statusFilter === 'active' && !a.isActive) return false;
            if (statusFilter === 'inactive' && a.isActive) return false;
            if (jobTypeFilter !== 'all' && !a._jobTypes.has(jobTypeFilter)) return false;
            // Every term must match somewhere, so "text repair" narrows rather than widens.
            return terms.every(t => a._search.includes(t));
        });
    }, [decorated, statusFilter, jobTypeFilter, search]);

    const filteredAutomations = useMemo(
        () => preCategory.filter(a => categoryFilter === 'all' || a._category === categoryFilter),
        [preCategory, categoryFilter]
    );

    const grouped = useMemo(() => {
        const result = new Map();
        for (const cat of CATEGORIES) result.set(cat.key, []);
        for (const a of filteredAutomations) {
            result.get(a._category)?.push(a);
        }
        return result;
    }, [filteredAutomations]);

    // Counts per category, used for the chip filter labels.
    const categoryCounts = useMemo(() => {
        const counts = {};
        for (const cat of CATEGORIES) counts[cat.key] = 0;
        for (const a of preCategory) {
            counts[a._category] = (counts[a._category] || 0) + 1;
        }
        return counts;
    }, [preCategory]);

    // Job-type chip counts, likewise narrowed by everything except the type filter.
    const jobTypeCounts = useMemo(() => {
        const terms = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
        const base = decorated.filter(a => {
            if (statusFilter === 'active' && !a.isActive) return false;
            if (statusFilter === 'inactive' && a.isActive) return false;
            if (categoryFilter !== 'all' && a._category !== categoryFilter) return false;
            return terms.every(t => a._search.includes(t));
        });
        const counts = {};
        for (const t of PROJECT_TYPES) counts[t.key] = base.filter(a => a._jobTypes.has(t.key)).length;
        return counts;
    }, [decorated, statusFilter, categoryFilter, search]);

    const stats = {
        total: automations.length,
        active: automations.filter(a => a.isActive).length,
        inactive: automations.filter(a => !a.isActive).length,
    };

    const getActionSummary = (actionsJson) => {
        try {
            const actions = JSON.parse(actionsJson);
            if (actions.length === 0) return 'No actions';
            const counts = actions.reduce((acc, action) => {
                acc[action.type] = (acc[action.type] || 0) + 1;
                return acc;
            }, {});
            return Object.entries(counts)
                .map(([type, count]) => `${count} ${type.replace('Create', '').replace('Send', '')}${count > 1 ? 's' : ''}`)
                .join(', ');
        } catch {
            return 'Invalid actions';
        }
    };

    const getTriggerSummary = (automation) => {
        try {
            const trigger = automation.triggerType || '';
            const conditions = JSON.parse(automation.triggerConditions || '{}');
            const friendly = FRIENDLY_TRIGGER[trigger] || trigger || 'Custom';
            if (conditions.toStatus) {
                return `${friendly} → ${conditions.toStatus}`;
            }
            if (conditions.taskTitle) {
                return `${friendly}: "${conditions.taskTitle}"`;
            }
            return friendly;
        } catch {
            return 'Invalid conditions';
        }
    };

    if (loading) {
        return (
            <div className="automation-manager">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading automation dashboard...</p>
                </div>
            </div>
        );
    }

    const renderCard = (automation) => (
        <div key={automation.id} className={`automation-card ${automation.isActive ? 'active' : 'inactive'}`}>
            <div className="card-status-bar"></div>
            <div className="card-header">
                <div className="automation-header-info">
                    <div className="automation-info">
                        <h3 className="automation-name">{automation.name}</h3>
                        {automation.description && (
                            <p className="automation-description">{automation.description}</p>
                        )}
                    </div>
                    <div className={`automation-status ${automation.isActive ? 'active' : 'inactive'}`}>
                        <div className="status-dot"></div>
                        {automation.isActive ? 'Active' : 'Inactive'}
                    </div>
                </div>
            </div>
            <div className="card-content">
                <div className="automation-metrics">
                    <div className="metric">
                        <div className="metric-value">
                            {JSON.parse(automation.actions || '[]').length}
                        </div>
                        <div className="metric-label">Actions</div>
                    </div>
                    <div className="metric">
                        <div className="metric-value">
                            {automation.delayDays || automation.delayMinutes ? '⏱' : '⚡'}
                        </div>
                        <div className="metric-label">Execution</div>
                    </div>
                </div>
                <div className="automation-details">
                    <div className="detail-item">
                        <span className="detail-label">Trigger</span>
                        <span className="detail-value trigger-value">{getTriggerSummary(automation)}</span>
                    </div>
                    <div className="detail-item">
                        <span className="detail-label">Actions</span>
                        <span className="detail-value actions-value">{getActionSummary(automation.actions)}</span>
                    </div>
                    {(automation.delayDays || automation.delayMinutes) && (
                        <div className="detail-item">
                            <span className="detail-label">Delay</span>
                            <span className="detail-value delay-value">
                                {automation.delayDays && `${automation.delayDays}d `}
                                {automation.delayMinutes && `${automation.delayMinutes}m`}
                            </span>
                        </div>
                    )}
                </div>
            </div>
            <div className="card-footer">
                <div className="created-info">
                    Created {new Date(automation.createdAt).toLocaleDateString()}
                </div>
                <div className="card-actions">
                    {openMenuId === automation.id && (
                        <div className="automation-menu-backdrop" onClick={() => setOpenMenuId(null)} />
                    )}
                    <button
                        className="automation-menu-trigger"
                        onClick={() => setOpenMenuId(prev => prev === automation.id ? null : automation.id)}
                        aria-label="Automation actions"
                    >
                        &bull;&bull;&bull;
                    </button>
                    {openMenuId === automation.id && (
                        <div className="automation-menu-dropdown">
                            <button className="automation-menu-item" onClick={() => { setOpenMenuId(null); handleEdit(automation); }}>Edit</button>
                            <button className="automation-menu-item" onClick={() => { setOpenMenuId(null); handleToggleActive(automation); }}>
                                {automation.isActive ? 'Deactivate' : 'Activate'}
                            </button>
                            <button className="automation-menu-item automation-menu-delete" onClick={() => { setOpenMenuId(null); handleDelete(automation); }}>Delete</button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    const noFiltersApplied =
        statusFilter === 'all' && categoryFilter === 'all' && jobTypeFilter === 'all' && !search.trim();

    const clearFilters = () => {
        setStatusFilter('all');
        setCategoryFilter('all');
        setJobTypeFilter('all');
        setSearch('');
    };

    const visibleCategories = CATEGORIES.filter(cat => {
        if (categoryFilter !== 'all' && categoryFilter !== cat.key) return false;
        return (grouped.get(cat.key) || []).length > 0;
    });

    return (
        <div className="automation-manager">
            <div className="manager-header">
                <div className="header-content">
                    <div className="header-info">
                        <h1 className="page-title">
                            <div className="title-icon">⚡</div>
                            Automation Control Center
                        </h1>
                        <p className="page-subtitle">
                            Manage and monitor your automated workflows
                        </p>
                    </div>
                </div>

                <div className="stats-dashboard">
                    <div className="stat-card total">
                        <div className="stat-number">{stats.total}</div>
                        <div className="stat-label">Total Automations</div>
                    </div>
                    <div className="stat-card active">
                        <div className="stat-number">{stats.active}</div>
                        <div className="stat-label">Active & Running</div>
                    </div>
                    <div className="stat-card inactive">
                        <div className="stat-number">{stats.inactive}</div>
                        <div className="stat-label">Inactive</div>
                    </div>
                </div>
            </div>

            {/* Search */}
            <div className="automation-search-bar">
                <span className="automation-search-icon" aria-hidden="true">🔍</span>
                <input
                    type="search"
                    className="automation-search-input"
                    placeholder="Search automations by name, description, or message text…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    aria-label="Search automations"
                />
                {search && (
                    <button className="automation-search-clear" onClick={() => setSearch('')} aria-label="Clear search">
                        ✕
                    </button>
                )}
            </div>

            {/* Status filter row + Create button */}
            <div className="controls-bar">
                <div className="filter-controls">
                    <button className={`filter-tab ${statusFilter === 'all' ? 'active' : ''}`} onClick={() => setStatusFilter('all')}>All Statuses</button>
                    <button className={`filter-tab ${statusFilter === 'active' ? 'active' : ''}`} onClick={() => setStatusFilter('active')}>Active Only</button>
                    <button className={`filter-tab ${statusFilter === 'inactive' ? 'active' : ''}`} onClick={() => setStatusFilter('inactive')}>Inactive Only</button>
                </div>
                <div className="action-controls">
                    <button className="btn-create-automation" onClick={() => setShowBuilder(true)}>
                        <div className="create-icon">+</div>
                        Create New Automation
                    </button>
                </div>
            </div>

            {/* Job type filter chips. An automation with no type condition counts under
                every type, because that is genuinely when it fires. */}
            <div className="category-chips job-type-chips">
                <button
                    className={`category-chip ${jobTypeFilter === 'all' ? 'active' : ''}`}
                    onClick={() => setJobTypeFilter('all')}
                >
                    All Job Types
                </button>
                {PROJECT_TYPES.map(t => (
                    <button
                        key={t.key}
                        className={`category-chip ${jobTypeFilter === t.key ? 'active' : ''}`}
                        onClick={() => setJobTypeFilter(t.key)}
                    >
                        {t.label} ({jobTypeCounts[t.key] || 0})
                    </button>
                ))}
            </div>

            {/* Category filter chips */}
            <div className="category-chips">
                <button
                    className={`category-chip ${categoryFilter === 'all' ? 'active' : ''}`}
                    onClick={() => setCategoryFilter('all')}
                >
                    All ({decorated.length})
                </button>
                {CATEGORIES.map(cat => {
                    const count = categoryCounts[cat.key] || 0;
                    if (count === 0) return null;
                    return (
                        <button
                            key={cat.key}
                            className={`category-chip ${categoryFilter === cat.key ? 'active' : ''}`}
                            onClick={() => setCategoryFilter(cat.key)}
                        >
                            <span className="chip-icon">{cat.icon}</span> {cat.label} ({count})
                        </button>
                    );
                })}
            </div>

            {/* Grouped sections */}
            {filteredAutomations.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-icon">🤖</div>
                    <h3>No Automations Found</h3>
                    <p>
                        {noFiltersApplied
                            ? "Get started by creating your first automation."
                            : search
                                ? `Nothing matches “${search}”. Try fewer words, or clear the filters.`
                                : "No automations match the current filters."}
                    </p>
                    {noFiltersApplied ? (
                        <button className="btn-create-first" onClick={() => setShowBuilder(true)}>
                            Create Your First Automation
                        </button>
                    ) : (
                        <button className="btn-create-first" onClick={clearFilters}>
                            Clear Filters
                        </button>
                    )}
                </div>
            ) : (
                visibleCategories.map(cat => {
                    const items = grouped.get(cat.key) || [];
                    return (
                        <section key={cat.key} className="automation-category-section">
                            <div className="category-section-header">
                                <span className="category-section-icon">{cat.icon}</span>
                                <h2 className="category-section-title">{cat.label}</h2>
                                <span className="category-section-count">{items.length}</span>
                            </div>
                            <div className="automations-grid">
                                {items.map(renderCard)}
                            </div>
                        </section>
                    );
                })
            )}

            <AutomationBuilder
                isOpen={showBuilder}
                onClose={handleCloseBuilder}
                editingAutomation={editingAutomation}
            />
        </div>
    );
};

export default AutomationManager;
